import React, { useState, useEffect } from 'react';
import axios from 'axios';
import swal from 'sweetalert';
import moment from 'moment';
import { Tabs, Tab } from 'react-bootstrap';

import { getCookie } from '../common_component/cookie.js';
import { useAuthData } from "./auth-provider.js";
import PageError from "../common_component/PageError.js";
import PageLoader from "../common_component/PageLoader.js";

const RAZORPAY_CHECKOUT_URL = "https://checkout.razorpay.com/v1/checkout.js";

const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }
        const script = document.createElement("script");
        script.src = RAZORPAY_CHECKOUT_URL;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

function CommunicationCredits() {
    const [state, setState] = useState(2);
    const [activeTab, setActiveTab] = useState("credits");
    const [credit, setCredit] = useState({ whatsapp_credits: 0, sms_credits: 0 });
    const [config, setConfig] = useState(null);

    const [whatsappQty, setWhatsappQty] = useState("");
    const [smsQty, setSmsQty] = useState("");
    const [purchaseButtonState, setPurchaseButtonState] = useState(false);

    const [categories, setCategories] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [isDefaultPermission, setIsDefaultPermission] = useState(false);
    const [permissionButtonState, setPermissionButtonState] = useState(false);

    const [transactions, setTransactions] = useState([]);
    const [transactionType, setTransactionType] = useState("purchase");
    const [transactionPage, setTransactionPage] = useState(1);
    const [transactionTotal, setTransactionTotal] = useState(0);
    const transactionLimit = 10;

    var page_name = "communication-credits";

    const { user, read, write, visibility, organization_id, organization_name } = useAuthData(page_name)

    const headers = {
        headers: {
            "Content-Type": "application/json",
            "Accept": "*/*",
            "token": getCookie("access_token")
        }
    }

    async function getCreditDetails() {
        const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/get-communication-credit-details`, { organization_id: organization_id, page_name: page_name }, headers);
        if (res.data.operation !== "success") {
            throw new Error(res.data.message);
        }
        setCredit(res.data.data.credit);
        setConfig(res.data.data.config);
    }

    async function getPermissions() {
        const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/get-communication-permissions`, { organization_id: organization_id, page_name: page_name }, headers);
        if (res.data.operation !== "success") {
            throw new Error(res.data.message);
        }
        setCategories(res.data.data.categories);
        setPermissions(res.data.data.permissions);
        setIsDefaultPermission(res.data.data.is_default);
    }

    async function getTransactions(type = transactionType, page = transactionPage) {
        const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/get-communication-credit-transactions`, { organization_id: organization_id, page_name: page_name, type: type, page: page, limit: transactionLimit }, headers);
        if (res.data.operation === "success") {
            setTransactions(res.data.data);
            setTransactionTotal(res.data.total);
        }
    }

    useEffect(() => {
        if ((read || write) && (user.email && visibility)) {
            Promise.all([getCreditDetails(), getPermissions(), getTransactions()])
                .then(() => {
                    setState(1);
                })
                .catch((err) => {
                    console.log(err);
                    setState(0);
                })
        }
    }, [user, visibility, read, write])

    const whatsappCount = parseInt(whatsappQty) || 0;
    const smsCount = parseInt(smsQty) || 0;
    // calculated in paise, same as server, so the displayed total matches the razorpay order
    const whatsappAmountPaise = config ? whatsappCount * Math.round(config.whatsapp_price * 100) : 0;
    const smsAmountPaise = config ? smsCount * Math.round(config.sms_price * 100) : 0;
    const baseAmountPaise = whatsappAmountPaise + smsAmountPaise;
    const gstAmountPaise = config ? Math.round(baseAmountPaise * config.gst_percentage / 100) : 0;
    const whatsappAmount = whatsappAmountPaise / 100;
    const smsAmount = smsAmountPaise / 100;
    const baseAmount = baseAmountPaise / 100;
    const gstAmount = gstAmountPaise / 100;
    const totalAmount = (baseAmountPaise + gstAmountPaise) / 100;

    const validatePurchase = () => {
        if (whatsappCount === 0 && smsCount === 0) {
            return "Please enter credits to purchase";
        }
        if (whatsappCount > 0 && whatsappCount < config.whatsapp_min_purchase) {
            return `Minimum ${config.whatsapp_min_purchase} WhatsApp credits must be purchased`;
        }
        if (smsCount > 0 && smsCount < config.sms_min_purchase) {
            return `Minimum ${config.sms_min_purchase} SMS credits must be purchased`;
        }
        if (whatsappCount > config.max_purchase || smsCount > config.max_purchase) {
            return `Maximum ${config.max_purchase} credits can be purchased at a time`;
        }
        return null;
    }

    const verifyPayment = (response) => {
        const object = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            organization_id: organization_id,
            page_name: page_name
        };

        axios.post(`${process.env.REACT_APP_SERVER_URL}/verify-communication-credit-payment`, object, headers).then((res) => {
            setPurchaseButtonState(false);
            if (res.data.operation === "success") {
                swal("Great!", res.data.message, "success").then(() => {
                    setWhatsappQty("");
                    setSmsQty("");
                    getCreditDetails();
                    setTransactionType("purchase");
                    setTransactionPage(1);
                    getTransactions("purchase", 1);
                });
            }
            else {
                swal("Oops!", res.data.message, "error");
            }
        })
            .catch((error) => {
                setPurchaseButtonState(false);
                console.log(error);
                swal("Oops!", "Payment verification failed. If amount is deducted, credits will be updated shortly.", "error");
            });
    }

    const purchaseCredits = async () => {
        const error = validatePurchase();
        if (error) {
            swal("Warning!", error, "warning");
            return;
        }

        setPurchaseButtonState(true);

        const loaded = await loadRazorpayScript();
        if (!loaded) {
            setPurchaseButtonState(false);
            swal("Oops!", "Unable to load payment gateway. Please check your connection.", "error");
            return;
        }

        const object = { whatsapp_credits: whatsappCount, sms_credits: smsCount, organization_id: organization_id, page_name: page_name };

        axios.post(`${process.env.REACT_APP_SERVER_URL}/create-communication-credit-order`, object, headers).then((res) => {
            if (res.data.operation !== "success") {
                setPurchaseButtonState(false);
                swal("Oops!", res.data.message, "error");
                return;
            }

            const order = res.data.data;
            const rzp = new window.Razorpay({
                key: order.razorpay_key_id,
                amount: order.amount,
                currency: order.currency,
                order_id: order.razorpay_order_id,
                name: "Communication Credits",
                description: `${whatsappCount} WhatsApp + ${smsCount} SMS credits`,
                prefill: { name: user.name, email: user.email },
                notes: { organization_name: organization_name },
                handler: verifyPayment,
                modal: {
                    ondismiss: () => setPurchaseButtonState(false)
                }
            });
            rzp.on("payment.failed", (response) => {
                setPurchaseButtonState(false);
                swal("Payment Failed", response.error.description || "Payment could not be completed", "error");
            });
            rzp.open();
        })
            .catch((error) => {
                setPurchaseButtonState(false);
                console.log(error);
                swal("Oops!", "Unable to create payment order", "error");
            });
    }

    const changePermission = (category, channel, value) => {
        setPermissions({ ...permissions, [category]: { ...permissions[category], [channel]: value } });
    }

    const savePermissions = () => {
        setPermissionButtonState(true);

        axios.post(`${process.env.REACT_APP_SERVER_URL}/save-communication-permissions`, { permissions: permissions, organization_id: organization_id, page_name: page_name }, headers).then((res) => {
            setPermissionButtonState(false);
            if (res.data.operation === "success") {
                swal("Great!", res.data.message, "success");
                getPermissions();
            }
            else {
                swal("Oops!", res.data.message, "error");
            }
        })
            .catch((error) => {
                setPermissionButtonState(false);
                console.log(error);
                swal("Oops!", "Unable to save permissions", "error");
            });
    }

    const changeTransactionType = (type) => {
        setTransactionType(type);
        setTransactionPage(1);
        getTransactions(type, 1);
    }

    const changeTransactionPage = (page) => {
        setTransactionPage(page);
        getTransactions(transactionType, page);
    }

    const totalPages = Math.max(Math.ceil(transactionTotal / transactionLimit), 1);

    const statusBadge = (status) => {
        const cls = status === "paid" || status === "sent" ? "badge-light-success" : status === "failed" ? "badge-light-danger" : "badge-light-warning";
        return <span className={`badge ${cls} text-capitalize`}>{status}</span>;
    }

    const categoryLabel = (key) => {
        const category = categories.find(x => x.key === key);
        return category ? category.label : key;
    }

    const balanceCard = (title, icon, color, count) => (
        <div className="col-md-6">
            <div className="card card-flush h-100 shadow-sm">
                <div className="card-body">
                    <div className="d-flex align-items-center mb-4">
                        <i className={`${icon} fs-2x text-${color} me-3`}></i>
                        <span className="fs-4 fw-bold text-gray-800">{title}</span>
                    </div>
                    <div className="fs-2hx fw-bold text-dark">{count}</div>
                    <div className="text-muted fw-semibold">Available credits</div>
                    {count === 0 && (
                        <div className="text-danger fs-7 mt-3">No credits left. {title} notifications will not be sent until you purchase credits.</div>
                    )}
                </div>
            </div>
        </div>
    )

    const permissionSwitch = (category, channel, configured) => (
        <div className="d-flex flex-column align-items-center">
            <label className="form-check form-switch form-check-custom form-check-solid">
                <input className="form-check-input" type="checkbox" checked={permissions[category.key] ? permissions[category.key][channel] : false} disabled={!write} onChange={(e) => changePermission(category.key, channel, e.target.checked)} />
            </label>
            {!configured && <span className="text-warning fs-8 mt-1">Not configured</span>}
        </div>
    )

    const creditsTab = () => (
        <>
            <div className="row g-5 mb-5">
                {balanceCard("WhatsApp", "bi bi-whatsapp", "success", credit.whatsapp_credits)}
                {balanceCard("SMS", "bi bi-chat-left-text", "primary", credit.sms_credits)}
            </div>

            {write && (
                <div className="card card-flush shadow-sm">
                    <div className="card-header">
                        <div className="card-title"><h3 className="fw-bold m-0">Purchase Credits</h3></div>
                    </div>
                    <div className="card-body">
                        <div className="row g-5">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">WhatsApp Credits</label>
                                <input type="number" min="0" step="1" className="form-control form-control-solid" placeholder={`Min ${config.whatsapp_min_purchase}`} value={whatsappQty} onChange={(e) => setWhatsappQty(e.target.value.replace(/\D/g, ""))} />
                                <div className="form-text">₹{config.whatsapp_price.toFixed(2)} per credit · Minimum {config.whatsapp_min_purchase}</div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">SMS Credits</label>
                                <input type="number" min="0" step="1" className="form-control form-control-solid" placeholder={`Min ${config.sms_min_purchase}`} value={smsQty} onChange={(e) => setSmsQty(e.target.value.replace(/\D/g, ""))} />
                                <div className="form-text">₹{config.sms_price.toFixed(2)} per credit · Minimum {config.sms_min_purchase}</div>
                            </div>
                        </div>

                        <div className="separator separator-dashed my-6"></div>

                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-4">
                            <div className="fs-6">
                                {whatsappCount > 0 && <div className="text-muted">WhatsApp: {whatsappCount} × ₹{config.whatsapp_price.toFixed(2)} = ₹{whatsappAmount.toFixed(2)}</div>}
                                {smsCount > 0 && <div className="text-muted">SMS: {smsCount} × ₹{config.sms_price.toFixed(2)} = ₹{smsAmount.toFixed(2)}</div>}
                                {baseAmount > 0 && <div className="text-muted">Subtotal: ₹{baseAmount.toFixed(2)}</div>}
                                {baseAmount > 0 && <div className="text-muted">GST ({config.gst_percentage}%): ₹{gstAmount.toFixed(2)}</div>}
                                <div className="fs-3 fw-bold text-dark mt-1">Total: ₹{totalAmount.toFixed(2)}</div>
                            </div>
                            <button className="btn btn-primary" onClick={purchaseCredits} data-kt-indicator={purchaseButtonState ? "on" : "off"} disabled={purchaseButtonState || totalAmount <= 0}>
                                <span className="indicator-label">Pay ₹{totalAmount.toFixed(2)}</span>
                                <span className="indicator-progress">Please wait...
                                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span></span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )

    const permissionsTab = () => (
        <div className="card card-flush shadow-sm">
            <div className="card-header align-items-center">
                <div className="card-title flex-column">
                    <h3 className="fw-bold m-0">Notification Permissions</h3>
                    <span className="text-muted fs-7 mt-1">Customers are notified automatically when the document is approved. One credit is used per message, only if credits are available.</span>
                </div>
                {isDefaultPermission && (
                    <div className="card-toolbar">
                        <span className="badge badge-light-info">Using default permissions</span>
                    </div>
                )}
            </div>
            <div className="card-body pt-0">
                <div className="table-responsive">
                    <table className="table table-row-dashed align-middle fs-6 gy-4">
                        <thead>
                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase">
                                <th>Category</th>
                                <th className="text-center">WhatsApp</th>
                                <th className="text-center">SMS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map((category) => (
                                <tr key={category.key}>
                                    <td className="fw-semibold text-gray-800">{category.label}</td>
                                    <td>{permissionSwitch(category, "whatsapp", category.configured.whatsapp)}</td>
                                    <td>{permissionSwitch(category, "sms", category.configured.sms)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {write && (
                    <div className="text-end">
                        <button className="btn btn-primary" onClick={savePermissions} data-kt-indicator={permissionButtonState ? "on" : "off"} disabled={permissionButtonState}>
                            <span className="indicator-label">Save Permissions</span>
                            <span className="indicator-progress">Please wait...
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span></span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    )

    const historyTab = () => (
        <div className="card card-flush shadow-sm">
            <div className="card-header align-items-center">
                <div className="card-title"><h3 className="fw-bold m-0">History</h3></div>
                <div className="card-toolbar">
                    <div className="btn-group">
                        <button className={`btn btn-sm ${transactionType === "purchase" ? "btn-primary" : "btn-light"}`} onClick={() => changeTransactionType("purchase")}>Purchases</button>
                        <button className={`btn btn-sm ${transactionType === "usage" ? "btn-primary" : "btn-light"}`} onClick={() => changeTransactionType("usage")}>Usage</button>
                    </div>
                </div>
            </div>
            <div className="card-body pt-0">
                {transactions.length == 0 ?
                    <PageError message='No Data Available' />
                    :
                    <div className="table-responsive">
                        {transactionType === "purchase" ?
                            <table className="table table-row-dashed align-middle fs-6 gy-4">
                                <thead>
                                    <tr className="text-start text-muted fw-bold fs-7 text-uppercase">
                                        <th>Date</th>
                                        <th>WhatsApp</th>
                                        <th>SMS</th>
                                        <th>Subtotal</th>
                                        <th>GST</th>
                                        <th>Total</th>
                                        <th>Payment ID</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((obj) => (
                                        <tr key={obj.transaction_id}>
                                            <td>{moment(obj.add_date_time).format("DD MMM YYYY, hh:mm A")}</td>
                                            <td>{obj.whatsapp_credits}</td>
                                            <td>{obj.sms_credits}</td>
                                            <td>{obj.base_amount != null ? `₹${Number(obj.base_amount).toFixed(2)}` : "-"}</td>
                                            <td>{obj.gst_amount != null ? `₹${Number(obj.gst_amount).toFixed(2)} (${obj.gst_percentage}%)` : "-"}</td>
                                            <td>₹{Number(obj.amount).toFixed(2)}</td>
                                            <td className="text-muted">{obj.razorpay_payment_id || "-"}</td>
                                            <td>{statusBadge(obj.status)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            :
                            <table className="table table-row-dashed align-middle fs-6 gy-4">
                                <thead>
                                    <tr className="text-start text-muted fw-bold fs-7 text-uppercase">
                                        <th>Date</th>
                                        <th>Channel</th>
                                        <th>Category</th>
                                        <th>Reference</th>
                                        <th>Recipient</th>
                                        <th>Credits</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((obj) => (
                                        <tr key={obj.transaction_id}>
                                            <td>{moment(obj.add_date_time).format("DD MMM YYYY, hh:mm A")}</td>
                                            <td>{obj.channel === "whatsapp" ? "WhatsApp" : "SMS"}</td>
                                            <td>{categoryLabel(obj.reference_type)}</td>
                                            <td>{obj.reference_number || obj.reference_id}</td>
                                            <td>{obj.recipient}</td>
                                            <td>{obj.credits}</td>
                                            <td title={obj.message}>{statusBadge(obj.status)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        }
                        <div className="d-flex justify-content-end align-items-center gap-3">
                            <button className="btn btn-sm btn-light" disabled={transactionPage <= 1} onClick={() => changeTransactionPage(transactionPage - 1)}>Previous</button>
                            <span className="text-muted fs-7">Page {transactionPage} of {totalPages}</span>
                            <button className="btn btn-sm btn-light" disabled={transactionPage >= totalPages} onClick={() => changeTransactionPage(transactionPage + 1)}>Next</button>
                        </div>
                    </div>
                }
            </div>
        </div>
    )

    return (
        <>
            <div>
                <div className="d-flex flex-stack mb-5">
                    <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">WhatsApp & SMS Credits</h1>
                </div>
                {
                    state == 1 ?
                        <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key)} className="mb-5">
                            <Tab eventKey="credits" title="Credits">
                                {creditsTab()}
                            </Tab>
                            <Tab eventKey="permissions" title="Notification Permissions">
                                {permissionsTab()}
                            </Tab>
                            <Tab eventKey="history" title="History">
                                {historyTab()}
                            </Tab>
                        </Tabs>
                        :
                        state == 2 ?
                            <PageLoader />
                            :
                            <PageError />
                }
            </div>
        </>
    );
}

export default CommunicationCredits;
